import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal, Spinner, Pagination } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  fetchEmergencyContacts,
  deleteEmergencyContact,
  selectContacts,
  selectMeta,
  selectLoading,
  selectError
} from '../../store/emergencyContact/emergencyContactSlice';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';

const EmergencyContactListComp = ({ data, viewMode }) => {
  // Always call hooks first
  const dispatch = useDispatch();
  const contacts = useSelector(selectContacts) || [];
  const meta = useSelector(selectMeta);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  let profileData = useSelector((state) => state.setting.setting.user_data);
  profileData = profileData ? JSON.parse(profileData) : null;
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    dispatch(fetchEmergencyContacts({ page: currentPage, limit: 10, student_id: getEffectiveStudentId(profileData) }));
  }, [dispatch, currentPage]);

  // Filter for active status if in profile view
  const filteredData = viewMode === 'profile' ? (Array.isArray(data) ? data.filter(item => item.status === 1) : []) : data;
  // If in profile view and no active data, show form instead of list
  if (viewMode === 'profile' && (!filteredData || filteredData.length === 0)) {
    return (
      <Container>
        <div className="text-center p-5">
          <p className="text-muted">No active Emergency Contact. Please add new data.</p>
          <Link to="/dashboard/emergency-contact/add">
            <Button variant="primary">Add Your First Contact</Button>
          </Link>
        </div>
      </Container>
    );
  }

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedContact(null);
  };

  const handleDelete = async (contactId, contactName) => {
    const result = await Swal.fire({
      title: 'Delete Contact?',
      text: `Are you sure you want to delete "${contactName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteEmergencyContact(contactId)).unwrap();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Emergency contact has been deleted.',
          timer: 2000
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error || 'Failed to delete contact'
        });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const renderPagination = () => {
    if (!meta || meta.last_page <= 1) return null;

    const items = [];
    for (let number = 1; number <= meta.last_page; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center mt-3">
        <Pagination.Prev
          key="prev"
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        />
        {items}
        <Pagination.Next
          key="next"
          disabled={currentPage === meta.last_page}
          onClick={() => handlePageChange(currentPage + 1)}
        />
      </Pagination>
    );
  };

  if (loading && (!contacts || contacts.length === 0)) {
    return (
      <Container>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-danger">Error: {error}</p>
            <Button variant="primary" onClick={() => dispatch(fetchEmergencyContacts({ page: 1, limit: 10, student_id: getEffectiveStudentId(profileData) }))}>
              Retry
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Row>
        <Col sm="12">
          {/* <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="header-title">
                <h4 className="card-title">Emergency Contacts</h4>
              </div>
              <Link to="/dashboard/emergency-contact/add">
                <Button variant="primary" size="sm">
                  <i className="icon material-symbols-outlined me-1" style={{verticalAlign: 'middle'}}>add</i>
                  Add Contact
                </Button>
              </Link>
            </Card.Header>
            <Card.Body> */}
              {!Array.isArray(contacts) || contacts.length === 0 ? (
                <div className="text-center py-5">
                  <i className="icon material-symbols-outlined" style={{ fontSize: '64px', color: '#ccc' }}>
                    contact_emergency
                  </i>
                  <p className="mt-3 text-muted">No emergency contacts found. Add your first contact.</p>
                  <Link to="/dashboard/emergency-contact/add">
                    <Button variant="primary">Add Your First Contact</Button>
                  </Link>
                </div>
              ) : (
                <Row>
                  {contacts.map((contact) => (
                    <Col md="12" key={contact.id} className="mb-3">
                      <Card className="border">
                        <Card.Body>
                          <Row className="align-items-center">
                            <Col md="8">
                              <h6 className="mb-2">
                                <i className="icon material-symbols-outlined me-2" style={{verticalAlign: 'middle', fontSize: '20px'}}>
                                  person
                                </i>
                                {contact.contact_name}
                                <Badge 
                                  bg={contact.status === 1 ? "success" : "secondary"} 
                                  className="ms-2"
                                >
                                  {contact.status === 1 ? "Active" : "Inactive"}
                                </Badge>
                              </h6>
                              <p className="mb-1">
                                <strong>Relation:</strong> {contact.relation || 'N/A'}
                              </p>
                              <p className="mb-1">
                                <strong>Phone:</strong> {contact.phone || 'N/A'}
                              </p>
                              <p className="mb-0 text-muted">
                                <small>Added: {formatDate(contact.created_at)}</small>
                              </p>
                            </Col>
                            <Col md="4" className="text-end">
                              <Button 
                                variant="info" 
                                size="sm" 
                                className="me-2"
                                onClick={() => handleViewDetails(contact)}
                              >
                                <i className="icon material-symbols-outlined me-1" style={{verticalAlign: 'middle'}}>visibility</i>
                                View
                              </Button>
                              <Link to={`/dashboard/emergency-contact/edit/${contact.id}`}>
                                <Button variant="primary" size="sm" className="me-2">
                                  <i className="icon material-symbols-outlined me-1" style={{verticalAlign: 'middle'}}>edit</i>
                                  Edit
                                </Button>
                              </Link>
                              <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => handleDelete(contact.id, contact.contact_name)}
                              >
                                <i className="icon material-symbols-outlined me-1" style={{verticalAlign: 'middle'}}>delete</i>
                                Delete
                              </Button>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
              {renderPagination()}
            {/* </Card.Body>
          </Card> */}
        </Col>
      </Row>

      {/* View Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Emergency Contact Details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedContact && (
            <>
              <Row className="mb-3">
                <Col md="6">
                  <strong>Status:</strong>
                  <Badge 
                    bg={selectedContact.status === 1 ? "success" : "secondary"} 
                    className="ms-2"
                  >
                    {selectedContact.status === 1 ? "Active" : "Inactive"}
                  </Badge>
                </Col>
                <Col md="6">
                  <strong>Contact ID:</strong> #{selectedContact.id}
                </Col>
              </Row>
              
              <hr />
              
              <h6 className="mb-3">Contact Information</h6>
              <Row className="mb-3">
                <Col md="12">
                  <p className="mb-2">
                    <strong>Contact Name:</strong> {selectedContact.contact_name || 'N/A'}
                  </p>
                  <p className="mb-2">
                    <strong>Relation:</strong> {selectedContact.relation || 'N/A'}
                  </p>
                  <p className="mb-2">
                    <strong>Phone Number:</strong> {selectedContact.phone || 'N/A'}
                  </p>
                </Col>
              </Row>

              <hr />

              <Row className="mb-3">
                <Col md="6">
                  <strong>Created:</strong> {formatDate(selectedContact.created_at)}
                </Col>
                <Col md="6">
                  <strong>Last Updated:</strong> {formatDate(selectedContact.updated_at)}
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          {selectedContact && (
            <Link to={`/dashboard/emergency-contact/edit/${selectedContact.id}`}>
              <Button variant="primary" onClick={handleCloseModal}>
                <i className="icon material-symbols-outlined me-1" style={{verticalAlign: 'middle'}}>edit</i>
                Edit
              </Button>
            </Link>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
export default EmergencyContactListComp;
